/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type ActionCloseArgs, type ColumnDef, type TableConfig, ButtonService, SlideWindowAbstract } from '@dm/core';
import { type Button, type SelectOption } from '@dm/core/types';
import { type Observable, of } from 'rxjs';

import { AddUserOrGroupComponent } from '../add-user-or-group/add-user-or-group.component';
import { type UserOrGroup, SelectTypeE } from '../user-quota';
import { UserQuotaStrService } from '../user-quota-str.service';

@Component({
    selector: 'app-select-user-or-group',
    templateUrl: './select-user-or-group.component.html',
    styleUrls: ['./select-user-or-group.component.scss'],
})
export class SelectUserOrGroupComponent extends SlideWindowAbstract implements OnInit {
    /**
     * 配额类型
     */
    @Input() quotaType: 'user' | 'group';
    /**
     * 上一次选中的类型
     */
    @Input() selectType: SelectTypeE;
    /**
     * 上一次选中的数据
     */
    @Input() data: UserOrGroup[];
    /**
     * 租户ID
     */
    @Input() vstoreId: string;

    closeArgs: {
        refresh: boolean;
        data: UserOrGroup[];
        selectType: SelectTypeE;
    } = {
            refresh: false,
            data: null,
            selectType: null,
        };
    i18n = this.str.i18n;
    winTitle: string;
    selectTypeLab: string;
    SelectTypeE = SelectTypeE;
    selectTypeOpts: SelectOption[];

    users: UserOrGroup[] = [];
    userGroups: UserOrGroup[] = [];

    tableConfig: TableConfig;
    tableColumns: ColumnDef[];
    selection: UserOrGroup[] = [];

    addBtn: Button;
    removeBtn: Button;

    showErrorMsg = false;

    constructor(
        injector: Injector,
        public str: UserQuotaStrService,
        private buttonService: ButtonService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.selectType === undefined) {
            this.selectType = SelectTypeE.Every;
        }

        if (this.quotaType === 'user') {
            this.winTitle = this.i18n.selectUserTitle;
            this.selectTypeOpts = this.str.userSelectTypeOpts;
            this.selectTypeLab = this.i18n.userLab;
        } else {
            this.winTitle = this.i18n.selectUserGroupTitle;
            this.selectTypeOpts = this.str.userGroupSelectTypeOpts;
            this.selectTypeLab = this.i18n.userGroupLab;
        }

        if (this.data) {
            const type = this.getType();

            if (type === 'user') {
                this.users = this.data;
            } else if (type === 'group') {
                this.userGroups = this.data;
            }
        }

        this.initBtns();
        this.initTable();

        this.pageReady = true;
        this.windowVisible = true;
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'select-user-add',
            label: this.i18n.addBtn,
            command: () => {
                this.add();
            },
        });

        this.removeBtn = this.buttonService.create({
            id: 'select-user-remove',
            label: this.i18n.removeBtn,
            disabledCheck: (data?: UserOrGroup) => {
                const items: UserOrGroup[] = data ? [data] : this.selection;
                return items.length === 0;
            },
            command: () => {
                this.remove();
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'id',
            selectionMode: 'checkbox',
        };

        this.tableColumns = [
            {
                header: this.i18n.nameLab,
                field: 'USRGRPOWNERNAME',
                render: this.str.userOrGroupRender,
                filterType: 'search',
                className: ['table-cell-text-pre'],
            },
            {
                header: this.i18n.typeLab,
                field: 'USRGRPTYPE',
                render: this.str.userOrGroupTypeRender,
            },
            {
                header: this.i18n.operationLab,
                field: 'operation',
                width: 80,
            },
        ];
    }

    /**
     * 获取当前所选的是类型
     * 返回所有、用户、用户组
     */
    getType(): string {
        if (this.selectType === SelectTypeE.Every) {
            return 'every';
        }
        return this.quotaType === 'user' && this.selectType === SelectTypeE.SpecificUserOrGroup ? 'user' : 'group';
    }

    /**
     * 类型切换，校验移除按钮等
     */
    onSelectTypeChange(): void {
        // 清空选中
        this.selection = [];
        this.removeBtn.checkDisabled();
        this.showErrorMsg = false;
    }

    /**
     * 添加
     */
    add(): void {
        const type = this.getType();

        this.doAction(AddUserOrGroupComponent, {
            type,
            quotaType: this.quotaType,
            vstoreId: this.vstoreId,
            data: type === 'user' ? this.users : this.userGroups,
            onClose: ({ refresh, data }: ActionCloseArgs) => {
                if (refresh) {
                    let arr: UserOrGroup[];

                    if (type === 'user') {
                        // 修改引用，触发表格刷新
                        this.users = [...this.users];
                        arr = this.users;
                    } else {
                        // 修改引用，触发表格刷新
                        this.userGroups = [...this.userGroups];
                        arr = this.userGroups;
                    }

                    data.forEach((a) => {
                        if (!arr.some((b) => b.id === a.id && b.usrOsType === a.usrOsType)) {
                            arr.push(a);
                        }
                    });

                    this.validate();
                }
            },
        });
    }

    /**
     * 移除
     * @param data
     */
    remove(data?: UserOrGroup): void {
        const items = data ? [data] : this.selection;
        if (this.getType() === 'user') {
            this.users = this.users.filter((a) => !items.some((b) => b.id === a.id && b.usrOsType === a.usrOsType));
        } else {
            this.userGroups = this.userGroups.filter((a) => !items.some((b) => b.id === a.id && b.usrOsType === a.usrOsType));
        }
    }

    /**
     * 校验
     */
    validate(): Observable<boolean> {
        const type = this.getType();

        if (type === 'user' && !this.users.length) {
            this.showErrorMsg = true;
            return of(false);
        }
        if (type === 'group' && !this.userGroups.length) {
            this.showErrorMsg = true;
            return of(false);
        }

        this.showErrorMsg = false;

        return of(true);
    }

    /**
     * 确定
     */
    onOk(): void {
        this.validate()
            .subscribe((valid) => {
                if (!valid) {
                    return;
                }

                const type = this.getType();

                this.closeArgs.refresh = true;
                this.closeArgs.selectType = this.selectType;

                switch (type) {
                    case 'every':
                        this.closeArgs.data = null;
                        break;
                    case 'user':
                        this.closeArgs.data = this.users;
                        break;
                    default:
                        this.closeArgs.data = this.userGroups;
                }

                this.windowVisible = false;
            });
    }
}
